// src/pages/ExpertCohortsPage.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cohortService } from '../api/services';
import DashboardLayout from '../components/layout/DashboardLayout';
import { FolderOpen, Users, Video, PenLine, ArrowRight, Loader2, AlertCircle, Award } from 'lucide-react';
import toast from 'react-hot-toast';

const ExpertCohortsPage = () => {
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cohortService.getMyCohorts()
      .then((res) => {
        setCohorts(res.data?.data?.cohorts ?? res.data?.cohorts ?? []);
      })
      .catch((err) => toast.error(err.response?.data?.message || 'Could not load your cohorts'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout title="My Cohorts & Classes">
      <div className="space-y-6 max-w-5xl">
        <div>
          <h2 className="font-serif text-2xl font-bold text-forest-900">Cohorts & Classes</h2>
          <p className="text-xs text-forest-500 mt-0.5">
            Manage your cohort classrooms, organize live online lectures, and assign coursework/IA tasks.
          </p>
        </div>

        {loading ? (
          <div className="py-20 text-center">
            <Loader2 size={32} className="animate-spin mx-auto text-forest-500" />
          </div>
        ) : cohorts.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 border border-cream-200 text-center space-y-3">
            <FolderOpen size={40} className="mx-auto text-forest-300" />
            <h3 className="font-serif text-lg font-semibold text-forest-900">No Cohorts Assigned Yet</h3>
            <p className="text-xs text-forest-500 max-w-md mx-auto leading-relaxed">
              You haven't been assigned to any cohorts yet. Once an administrator or another lecturer assigns you to a cohort, it will appear here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {cohorts.map((c) => {
              const roleTitle = c.myCohortRole === 'PRIMARY_EXPERT'
                ? 'Lecturer'
                : c.myCohortRole === 'TEACHING_ASSISTANT'
                ? 'Teaching Assistant'
                : 'Guest Lecturer';

              return (
                <div
                  key={c.id}
                  className="bg-white rounded-2xl p-6 border border-cream-200 hover:border-forest-400 hover:shadow-md transition-all flex flex-col justify-between space-y-4"
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                        c.myCohortRole === 'PRIMARY_EXPERT'
                          ? 'bg-amber-100 text-amber-800'
                          : c.myCohortRole === 'TEACHING_ASSISTANT'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-forest-100 text-forest-800'
                      }`}>
                        {roleTitle}
                      </span>
                      <span className="text-xs text-forest-400 flex items-center gap-1">
                        <Users size={12} /> {c._count?.members || 0} members
                      </span>
                    </div>

                    <h3 className="font-serif text-xl font-bold text-forest-900">{c.name}</h3>

                    {c.description && (
                      <p className="text-xs text-forest-600 line-clamp-2 leading-relaxed">
                        {c.description}
                      </p>
                    )}
                  </div>

                  <div className="pt-3 border-t border-cream-100 flex items-center justify-between">
                    <span className="text-[11px] text-forest-400">Classroom, Lectures & IA</span>
                    <Link
                      to={`/expert/cohorts/${c.id}`}
                      className="btn-primary text-xs py-2 px-3.5 inline-flex items-center gap-1.5"
                    >
                      Open Classroom <ArrowRight size={13} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ExpertCohortsPage;
